import { useEffect, useRef, useState } from "react";
import imgUrl from "../../assets/images/imgUrl";
import * as d3 from "d3";

export const CircleChart = ({ onNodeSelect, activeNodeId, nodes }) => {
  const svgRef = useRef(null);
  const [positionedNodes, setPositionedNodes] = useState();

  const centerX = 300;
  const centerY = 300;
  const radius = 200;
  const innerRadius = 80; // inner circle radius for media

  // Calculate and set node positions once
  useEffect(() => {
    if (positionedNodes) return;

    const nodePositions = nodes.map((node, index) => {
      const angle = (index / nodes.length) * 2 * Math.PI - Math.PI / 2;
      const labelDistance = radius + 51; // Fixed distance from the outer circle
      return {
        ...node,
        angle,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
        labelX: centerX + labelDistance * Math.cos(angle),
        labelY: centerY + labelDistance * Math.sin(angle),
      };
    });

    setPositionedNodes(nodePositions);
  }, [nodes, centerX, centerY, radius, positionedNodes]);

  // Render nodes
  useEffect(() => {
    if (!svgRef.current || !positionedNodes) return;

    const svg = d3.select(svgRef.current);
    svg.select(".nodes").selectAll(".node-group").remove();

    const nodeGroups = svg
      .select(".nodes")
      .selectAll(".node-group")
      .data(positionedNodes)
      .enter()
      .append("g")
      .attr("class", "node-group")
      .style("cursor", "pointer")
      .on("click", (event, d) => onNodeSelect(d));

    // Circles
    nodeGroups
      .append("circle")
      .attr("cx", (d) => d.x)
      .attr("cy", (d) => d.y)
      .attr("r", 14)
      .attr("fill", (d) => (d.id === activeNodeId ? "#4a148c" : "#6E7A8C"))
      .attr("stroke", "#FFFFFF")
      .attr("stroke-width", 3);

    // Labels
    nodeGroups
      .append("text")
      .attr("x", (d) => d.labelX)
      .attr("y", (d) => d.labelY)
      .attr("text-anchor", "middle")
      .attr("font-size", "12px")
      .attr("font-weight", "500")
      .attr("fill", (d) => (d.id === activeNodeId ? "#8B43D9" : "#6E7A8C"))
      .text((d) => d.label)
      .call(wrap, 80);
  }, [positionedNodes, onNodeSelect, activeNodeId]);

  // Update colors
  useEffect(() => {
    if (!svgRef.current || !positionedNodes) return;

    const svg = d3.select(svgRef.current);

    svg
      .select(".nodes")
      .selectAll(".node-group circle")
      .attr("fill", (d) => (d.id === activeNodeId ? "#4a148c" : "#6E7A8C"))
      .attr("stroke", (d) => (d.id === activeNodeId ? "#8B43D9" : "#6E7A8C"));

    svg
      .select(".nodes")
      .selectAll(".node-group text")
      .attr("fill", (d) => (d.id === activeNodeId ? "#8B43D9" : "#6E7A8C"));
  }, [activeNodeId, positionedNodes]);

  // Draw arcs (stop at inner circle border)
  useEffect(() => {
    if (!svgRef.current || !positionedNodes) return;

    const svg = d3.select(svgRef.current);
    svg.select(".arcs").selectAll(".node-arc").remove();

    const strandOffset = 2;

    positionedNodes.forEach((node, index) => {
      const dx = node.x - centerX;
      const dy = node.y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const perpUnitX = -dy / distance;
      const perpUnitY = dx / distance;

      const startX = centerX;
      const startY = centerY;

      // Set strands: 1 for even index, 2 for odd index, 3 for only the first node (index 0)
      const strands = index < 2 ? 3 : index % 2 === 0 ? 1 : 2;

      for (let s = -1; s <= 1; s += 2 / (strands - 1)) {
        const offset = s * strandOffset;
        const endX = node.x + offset * perpUnitX;
        const endY = node.y + offset * perpUnitY;

        const cdx = endX - startX;
        const cdy = endY - startY;
        const cdist = Math.sqrt(cdx * cdx + cdy * cdy);

        const controlOffset = cdist * 0.2 * s;
        const cperpX = (-cdy / cdist) * controlOffset;
        const cperpY = (cdx / cdist) * controlOffset;

        let arcPath;
        if (strands === 1) {
          // Slight curve for single strand
          const cdx = endX - startX;
          const cdy = endY - startY;
          const cdist = Math.sqrt(cdx * cdx + cdy * cdy);
          const controlOffset = cdist * 0.15;
          const cperpX = (-cdy / cdist) * controlOffset;
          const cperpY = (cdx / cdist) * controlOffset;
          const cp1x = startX + cdx * 0.5 + cperpX;
          const cp1y = startY + cdy * 0.5 + cperpY;
          arcPath = `M ${startX} ${startY} Q ${cp1x} ${cp1y}, ${endX} ${endY}`;
        } else {
          const rand = 0.5; // random 0–1 for variability
          const distFactor1 = 0.2 + 0.1 * rand; // 0.4–0.7 fraction along the line
          const distFactor2 = 0.4 + 0.1 * rand; // 0.2–0.4 fraction near center
          const strength = 1 + rand * 1; // strong outward bow (8–20x)

          const cp1x = startX + cdx * distFactor1 + cperpX * strength;
          const cp1y = startY + cdy * distFactor1 + cperpY * strength;

          const cp2x = endX - cdx * distFactor2 + cperpX * strength;
          const cp2y = endY - cdy * distFactor2 + cperpY * strength;

          arcPath = `M ${startX} ${startY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${endX} ${endY}`;
        }

        const path = svg
          .select(".arcs")
          .append("path")
          .attr("class", `node-arc node-arc-${node.id}`)
          .attr("d", arcPath)
          .attr("stroke", node.id === activeNodeId ? "#8B43D9" : "#6E7A8C")
          .attr("stroke-width", 1)
          .attr("fill", "none")
          .attr("opacity", 0.6)
          .attr("stroke-linecap", "round");

        if (node.id === activeNodeId) {
          const pathLength = path.node().getTotalLength();
          path
            .style("stroke-dasharray", `${pathLength} ${pathLength}`)
            .style("stroke-dashoffset", String(pathLength))
            .style("opacity", "0.9")
            .style("filter", "drop-shadow(0 2px 8px rgba(139, 67, 217, 0.3))")
            .transition()
            .duration(800)
            .ease(d3.easeCubicOut)
            .style("stroke-dashoffset", "6");
        }
      }
    });
  }, [activeNodeId, positionedNodes, centerX, centerY, innerRadius]);

  return (
    <div className="relative">
      <svg ref={svgRef} width={600} height={600} className="overflow-visible">
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#E8ECF4"
          strokeWidth={5}
          opacity={0.9}
        />

        <g className="arcs" />

        {/* Inner circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="#69009f"
          stroke="#E8ECF4"
          strokeWidth={2}
        />

        {/* Center image */}
        <defs>
          <clipPath id="centerClip">
            <circle cx={centerX} cy={centerY} r={innerRadius - 5} />
          </clipPath>
        </defs>
        <image
          // x={centerX - (innerRadius - 25)}
          // y={centerY - (innerRadius - 25)}
          x={259}
          y={255}
          width={90}
          height={90}
          // href={nodes.find((n) => n.id === activeNodeId)?.mediaSrc}
          href={imgUrl.dure_logo_symbol}
          // clipPath="url(#centerClip)"
          preserveAspectRatio="xMidYMid meet"
        />

        <g className="nodes" />
      </svg>
    </div>
  );
};

// D3 text wrap helper
function wrap(text, width) {
  text.each(function () {
    const text = d3.select(this);
    const words = text.text().split(/\s+/).reverse();
    let word;
    let line = [];
    let lineNumber = 0;
    const lineHeight = 1.1;
    const y = text.attr("y");
    const dy = parseFloat(text.attr("dy") || "0");
    let tspan = text
      .text(null)
      .append("tspan")
      .attr("x", text.attr("x"))
      .attr("y", y)
      .attr("dy", dy + "em");

    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node()?.getComputedTextLength() > width || line.length > 2) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = text
          .append("tspan")
          .attr("x", text.attr("x"))
          .attr("y", y)
          .attr("dy", ++lineNumber * lineHeight + dy + "em")
          .text(word);
      }
    }
  });
}
